import { client } from '../sanity'

export interface EtsyConfigSetListItem {
  _id: string
  _type: 'etsyConfigSet'
  name: string
  description?: string
  _createdAt?: string
  _updatedAt?: string
  productCount?: number
}

export interface EtsyConfigSetDetail {
  _id: string
  _type: 'etsyConfigSet'
  name: string
  description?: string
  originListingId?: string
  shipping_template_id?: string
  taxonomy_id?: string
  shop_section_id?: string
  return_policy_id?: string
  currency_code?: {
    _id: string
    _ref: string
    code?: string
    name?: string
  }
  language?: string
  who_made?: string
  when_made?: string
  is_supply?: boolean
  materials?: Array<{
    _id: string
    _ref: string
    name?: string
  }>
  tags?: Array<{
    _id: string
    _ref: string
    name?: string
  }>
  processing_min?: number
  processing_max?: number
  attributes?: Array<{
    property_id: number
    values: string[]
  }>
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Fetch Etsy config sets for product-channel mapping.
 * When brandId is provided, returns only config sets for that brand (or unassigned).
 */
export const getEtsyConfigSets = async (brandId?: string): Promise<EtsyConfigSetListItem[]> => {
  const brandFilter = brandId
    ? `&& (brand == null || brand._ref == $brandId)`
    : ''
  const configSets = await client.fetch(
    `*[_type == "etsyConfigSet" ${brandFilter}] | order(_createdAt desc) {
      _id,
      _type,
      name,
      description,
      _createdAt,
      _updatedAt
    }`,
    brandId ? { brandId } : {}
  )

  // Count products linked to each config set
  const configSetsWithCounts = await Promise.all(
    configSets.map(async (configSet: EtsyConfigSetListItem) => {
      const productCount = await client.fetch(
        `count(*[_type == "product" && channelMapping[].configSet._ref == $configSetId && channelMapping[].channel == "etsy"])`,
        { configSetId: configSet._id }
      )
      return {
        ...configSet,
        productCount: productCount || 0,
      }
    })
  )

  return configSetsWithCounts
}

/**
 * Fetch a single Etsy config set by ID.
 */
export const getEtsyConfigSetById = async (id: string): Promise<EtsyConfigSetDetail | null> => {
  return client.fetch(
    `*[_type == "etsyConfigSet" && _id == $id][0] {
      _id,
      _type,
      name,
      description,
      originListingId,
      shipping_template_id,
      taxonomy_id,
      shop_section_id,
      return_policy_id,
      currency_code-> {
        _id,
        _ref,
        code,
        name
      },
      language,
      who_made,
      when_made,
      is_supply,
      materials[]-> {
        _id,
        _ref,
        name
      },
      tags[]-> {
        _id,
        _ref,
        name
      },
      processing_min,
      processing_max,
      attributes,
      _createdAt,
      _updatedAt
    }`,
    { id }
  )
}

/**
 * Create a new Etsy config set with minimal defaults.
 * When brandId is provided, assigns the config set to that brand.
 */
export const createEtsyConfigSet = async (brandId?: string): Promise<{ _id: string }> => {
  // First, get the GBP currency reference
  const gbpCurrency = await client.fetch<{ _id: string }>(
    `*[_type == "currency" && code == "GBP"][0] { _id }`
  )

  if (!gbpCurrency) {
    throw new Error('GBP currency not found. Please create a currency with code "GBP" first.')
  }

  // Get a default tag (or create one if none exist)
  const defaultTag = await client.fetch<{ _id: string }>(
    `*[_type == "tag"][0] { _id }`
  )

  if (!defaultTag) {
    throw new Error('No tags found. Please create at least one tag first.')
  }

  const doc: Record<string, unknown> = {
    _type: 'etsyConfigSet' as const,
    name: 'New Config Set',
    description: 'Configuration set - please update with your settings',
    originListingId: '',
    sourceProductData: '',
    shipping_template_id: '',
    taxonomy_id: '',
    shop_section_id: '',
    return_policy_id: '',
    currency_code: {
      _type: 'reference' as const,
      _ref: gbpCurrency._id,
    },
    language: 'en',
    who_made: 'i_did',
    when_made: 'made_to_order',
    is_supply: false,
    materials: [],
    tags: [
      {
        _type: 'reference' as const,
        _ref: defaultTag._id,
      },
    ],
    processing_min: 1,
    processing_max: 3,
    attributes: [],
  }
  if (brandId) {
    doc.brand = { _type: 'reference' as const, _ref: brandId }
  }

  const created = await client.create(doc as any)
  return { _id: created._id }
}

/**
 * Fetch all currencies for dropdowns
 */
export interface Currency {
  _id: string
  _type: 'currency'
  code: string
  name: string
  symbol: string
}

export const getAllCurrencies = async (): Promise<Currency[]> => {
  return client.fetch(
    `*[_type == "currency"] | order(code asc) {
      _id,
      _type,
      code,
      name,
      symbol
    }`
  )
}

/**
 * Fetch all materials for dropdowns
 */
export interface Material {
  _id: string
  _type: 'material'
  name: string
}

export const getAllMaterials = async (): Promise<Material[]> => {
  return client.fetch(
    `*[_type == "material"] | order(name asc) {
      _id,
      _type,
      name
    }`
  )
}

/**
 * Fetch all tags for dropdowns
 */
export interface Tag {
  _id: string
  _type: 'tag'
  name: string
}

export const getAllTags = async (): Promise<Tag[]> => {
  return client.fetch(
    `*[_type == "tag"] | order(name asc) {
      _id,
      _type,
      name
    }`
  )
}

/**
 * Update an Etsy config set.
 */
export interface UpdateEtsyConfigSetInput {
  name?: string
  description?: string
  shipping_template_id?: string
  taxonomy_id?: string
  shop_section_id?: string
  return_policy_id?: string
  currency_code?: string | null // _id of currency reference
  who_made?: string
  when_made?: string
  is_supply?: boolean
  processing_min?: number
  processing_max?: number
  materials?: string[] // Array of material _ids
  tags?: string[] // Array of tag _ids
}

export const updateEtsyConfigSet = async (
  id: string,
  input: UpdateEtsyConfigSetInput
): Promise<void> => {
  const patch: any = {}

  if (input.name !== undefined) patch.name = input.name
  if (input.description !== undefined) patch.description = input.description
  if (input.shipping_template_id !== undefined)
    patch.shipping_template_id = input.shipping_template_id
  if (input.taxonomy_id !== undefined) patch.taxonomy_id = input.taxonomy_id
  if (input.shop_section_id !== undefined) patch.shop_section_id = input.shop_section_id
  if (input.return_policy_id !== undefined) patch.return_policy_id = input.return_policy_id
  if (input.currency_code !== undefined) {
    if (input.currency_code) {
      patch.currency_code = {
        _type: 'reference',
        _ref: input.currency_code,
      }
    } else {
      patch.currency_code = null
    }
  }
  if (input.who_made !== undefined) patch.who_made = input.who_made
  if (input.when_made !== undefined) patch.when_made = input.when_made
  if (input.is_supply !== undefined) patch.is_supply = input.is_supply
  if (input.processing_min !== undefined) patch.processing_min = input.processing_min
  if (input.processing_max !== undefined) patch.processing_max = input.processing_max

  if (input.materials !== undefined) {
    patch.materials = input.materials.map((materialId) => ({
      _type: 'reference',
      _ref: materialId,
    }))
  }

  if (input.tags !== undefined) {
    patch.tags = input.tags.map((tagId) => ({
      _type: 'reference',
      _ref: tagId,
    }))
  }

  await client.patch(id).set(patch).commit()
}

/**
 * Fetch products linked to a specific Etsy config set via channelMapping
 * Includes both published and draft products
 */
export interface ProductLinkedToConfigSet {
  _id: string
  _type: 'product'
  title?: string
  sku?: string
  rrp?: number
  isPrintOnDemand?: boolean
  etsyMapping?: {
    channel?: string
    status?: string
  }
  // Also fetch all channel mappings to show which channels reference this config set
  channelMapping?: Array<{
    channel?: string
    configSet?: {
      _ref?: string
    }
  }>
}

export const getProductsByConfigSet = async (
  configSetId: string
): Promise<ProductLinkedToConfigSet[]> => {
  // Include both published and draft products to show all references
  // Check all channel mappings, not just Etsy, to catch all references
  return client.fetch(
    `*[_type == "product" && channelMapping[].configSet._ref == $configSetId] | order(_updatedAt desc) {
      _id,
      _type,
      title,
      sku,
      rrp,
      isPrintOnDemand,
      channelMapping[] {
        channel,
        configSet {
          _ref
        }
      }
    }`,
    { configSetId }
  )
}

/**
 * Get all product IDs that reference this config set (for deletion validation)
 * This includes drafts and published documents
 * Checks all channel mappings, not just Etsy, since Sanity blocks deletion for any reference
 */
export const getProductIdsByConfigSet = async (configSetId: string): Promise<string[]> => {
  try {
    // Query for all products that reference this config set
    const products = await client.fetch(
      `*[_type == "product" && channelMapping[].configSet._ref == $configSetId]._id`,
      { configSetId }
    )
    
    console.log(`[getProductIdsByConfigSet] Found ${products?.length || 0} products referencing config set ${configSetId}:`, products)
    
    return products || []
  } catch (error) {
    console.error(`[getProductIdsByConfigSet] Error querying products for config set ${configSetId}:`, error)
    // Try a more permissive query as fallback
    try {
      const fallbackProducts = await client.fetch(
        `*[_type == "product" && defined(channelMapping) && count(channelMapping[configSet._ref == $configSetId]) > 0]._id`,
        { configSetId }
      )
      console.log(`[getProductIdsByConfigSet] Fallback query found ${fallbackProducts?.length || 0} products:`, fallbackProducts)
      return fallbackProducts || []
    } catch (fallbackError) {
      console.error(`[getProductIdsByConfigSet] Fallback query also failed:`, fallbackError)
      return []
    }
  }
}

/**
 * Delete an Etsy config set.
 * Checks for linked products first and provides helpful error messages.
 */
export const deleteEtsyConfigSet = async (id: string): Promise<void> => {
  try {
    // Check for linked products before attempting deletion
    const linkedProductIds = await getProductIdsByConfigSet(id)
    if (linkedProductIds.length > 0) {
      // Get product details for better error message
      const products = await client.fetch(
        `*[_id in $productIds] {
          _id,
          title,
          sku,
          "isDraft": _id match "drafts.*"
        }`,
        { productIds: linkedProductIds }
      )
      
      const productList = products
        .map((p: any) => `${p.title || p.sku || p._id}${p.isDraft ? ' (draft)' : ''}`)
        .join(', ')
      
      throw new Error(
        `Cannot delete config set: ${linkedProductIds.length} product(s) are still linked to it:\n${productList}\n\nPlease remove the config set from those products first.`
      )
    }

    await client.delete(id)
  } catch (error) {
    console.error('Failed to delete Etsy config set:', error)
    // If it's already our custom error, re-throw it
    if (error instanceof Error && error.message.includes('Cannot delete config set')) {
      throw error
    }
    // Otherwise, check if it's a Sanity reference error
    if (error instanceof Error && error.message.includes('cannot be deleted as there are references')) {
      // Try to get the actual product IDs to show in the error
      const linkedProductIds = await getProductIdsByConfigSet(id).catch(() => [])
      if (linkedProductIds.length > 0) {
        // Get product details for better error message
        const products = await client.fetch(
          `*[_id in $productIds] {
            _id,
            title,
            sku,
            "isDraft": _id match "drafts.*"
          }`,
          { productIds: linkedProductIds }
        ).catch(() => [])
        
        if (products.length > 0) {
          const productList = products
            .map((p: any) => `${p.title || p.sku || p._id}${p.isDraft ? ' (draft)' : ''}`)
            .join(', ')
          
          throw new Error(
            `Cannot delete config set: ${linkedProductIds.length} product(s) are still linked to it:\n${productList}\n\nPlease remove the config set from those products first.`
          )
        }
        
        throw new Error(
          `Cannot delete config set: ${linkedProductIds.length} product(s) are still linked to it. Product IDs: ${linkedProductIds.slice(0, 5).join(', ')}${linkedProductIds.length > 5 ? '...' : ''}. Please remove the config set from those products first.`
        )
      }
      throw new Error(
        `Cannot delete config set: There are still references to it. Please check that all products have been unlinked from this config set.`
      )
    }
    throw new Error(`Failed to delete config set: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

